import { useCallback, useEffect, useRef, useState } from "react";
import { MediaSurface } from "../ui/MediaSurface";
import { MASK } from "../../lib/replayMask";

/** How long to wait before asking again for a preview the server is still rendering. */
const RETRY_MS = 4000;

/**
 * A view's preview: the rendered loop, or the fact that it is being rendered.
 *
 * Every card on the home screen used to mount its view LIVE — each region its own
 * `/api/source/fetch` and its own `<video>`, so a library of eight four-region views was
 * thirty-odd of each before the quick setups underneath were counted. One file per view
 * replaces all of it, and the built-ins go through exactly the same route, so there is
 * one way a card shows what a view looks like.
 *
 * There is deliberately NO live-surface fallback. The pending case is a real state with a
 * real answer — "this is being made" — and rendering the whole view live while waiting
 * would reintroduce the exact cost the preview exists to remove, at the worst moment: a
 * fresh library, every card at once, while the encoder is already busy.
 *
 * A 404 means "not yet", not "broken": the route starts the render when it answers one, so
 * asking again is what eventually gets the file. It backs off on a fixed beat rather than
 * hammering — an encode takes seconds, and there is nothing to gain by asking sooner.
 *
 * Zero regions is a different answer entirely. The encoder skips an empty view on purpose
 * (`preview-render.ts`: nothing to composite), so polling `/preview` can never become
 * ready — the card used to spin "Preview generating…" forever on black. When the caller
 * knows the region count is zero, this mounts the empty state instead of the poller.
 */
export function ViewPreview({
  viewId,
  name,
  regionCount,
}: {
  viewId: string;
  name: string;
  /** Pass 0 to skip the poller — an empty view never gets a preview file. Omit (or any
   *  positive count) for the normal generate-and-retry path. Built-ins leave this off. */
  regionCount?: number;
}) {
  const empty = regionCount === 0;
  const [ready, setReady] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // A changed view is a changed preview, so start over rather than showing the old one.
  useEffect(() => {
    setReady(false);
    setAttempt(0);
  }, [viewId]);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const retryLater = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setAttempt((n) => n + 1), RETRY_MS);
  }, []);

  // Empty first: grey panel, short copy, no MediaSurface — same panel tone as the
  // "generating" overlay, without the spinner that would never stop.
  if (empty) {
    return (
      <div className="relative h-full w-full bg-[var(--color-panel-2)]">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-3 text-center">
          <span className="text-[11px] text-zinc-500">No regions yet</span>
          <span className="text-[10px] text-zinc-600">Add a region to preview</span>
          <span className="sr-only" {...MASK}>{`${name} has no regions to preview`}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full bg-black">
      {/* Keyed on the attempt so a retry genuinely re-requests rather than reusing the
          element's cached 404. */}
      <MediaSurface
        key={`${viewId}:${attempt}`}
        kind="video"
        src={`/api/views/${encodeURIComponent(viewId)}/preview`}
        fit="contain"
        loop
        muted
        onError={retryLater}
        onCanPlayThrough={() => setReady(true)}
      />
      {!ready ? (
        /* Grey, not black: the card's footer is black, and a black pending state made the
           two run together into one slab with a spinner floating in it. This reads as a
           frame with something coming. */
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[var(--color-panel-2)]">
          <span
            aria-hidden
            className="h-6 w-6 animate-spin rounded-full border-2 border-[var(--color-accent)]/25 border-t-[var(--color-accent)]"
          />
          <span className="text-[11px] text-zinc-500">Preview generating…</span>
          <span className="sr-only" {...MASK}>{`Preview for ${name} is being generated`}</span>
        </div>
      ) : null}
    </div>
  );
}
